import { Auth0Provider, useAuth0 } from "@auth0/auth0-react"
import "@fontsource-variable/plus-jakarta-sans"
import "@fontsource-variable/inter"
import { library } from "@fortawesome/fontawesome-svg-core"
import "@fortawesome/fontawesome-svg-core/styles.css"
import { fas } from "@fortawesome/free-solid-svg-icons"
library.add(fas)
import { CssBaseline, ThemeProvider } from "@mui/material"
import {
  DebugThemeContext,
  getInitialCustomTheme,
  persistCustomTheme,
} from "./app/hooks/useDebugTheme"
import {
  getDensityTokens,
  getInitialDensity,
  persistDensity,
  UiDensityContext,
  type UiDensity,
} from "./app/hooks/useUiDensity"
import {
  getInitialSidebarStyle,
  getSidebarTokens,
  persistSidebarStyle,
  SidebarStyleContext,
  type SidebarStyleKey,
} from "./app/hooks/useSidebarStyle"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import * as Sentry from "@sentry/react"
import React, { StrictMode, useCallback, useMemo, useState } from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router"
import { AppRoutes } from "./AppRoutes"
import { store } from "./app/apis"
import { useWebSocket } from "./hooks/useWebSocket"
import "./i18n/i18n"
import { defaultTheme, lightTheme } from "./theme"
import { setAuth0 } from "./utils/auth0Utils"
import { DevAuth0Provider } from "./utils/DevAuth0Provider"

const isAgenticMode = !!import.meta.env.VITE_AGENTIC
const AppAuth0Provider = isAgenticMode ? DevAuth0Provider : Auth0Provider

const AppWrapper = () => {
  const auth0 = useAuth0()
  setAuth0(auth0)
  useWebSocket()

  const [customTheme, setCustomThemeRaw] = useState(getInitialCustomTheme)
  const setCustomTheme = useCallback((enabled: boolean) => {
    setCustomThemeRaw(enabled)
    persistCustomTheme(enabled)
  }, [])
  const debugThemeCtx = useMemo(
    () => ({ customTheme, setCustomTheme }),
    [customTheme, setCustomTheme],
  )
  const activeTheme = customTheme ? lightTheme : defaultTheme

  const [density, setDensityRaw] = useState(getInitialDensity)
  const setDensity = useCallback((d: UiDensity) => {
    setDensityRaw(d)
    persistDensity(d)
  }, [])
  const densityCtx = useMemo(
    () => ({ density, setDensity, tokens: getDensityTokens(density) }),
    [density, setDensity],
  )

  const [sidebarStyle, setSidebarStyleRaw] = useState(getInitialSidebarStyle)
  const setSidebarStyle = useCallback((s: SidebarStyleKey) => {
    setSidebarStyleRaw(s)
    persistSidebarStyle(s)
  }, [])
  const sidebarCtx = useMemo(
    () => ({ style: sidebarStyle, setStyle: setSidebarStyle, colors: getSidebarTokens(sidebarStyle) }),
    [sidebarStyle, setSidebarStyle],
  )

  return (
    <DebugThemeContext.Provider value={debugThemeCtx}>
      <UiDensityContext.Provider value={densityCtx}>
        <SidebarStyleContext.Provider value={sidebarCtx}>
          <ThemeProvider theme={activeTheme}>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <AppRoutes />
            </LocalizationProvider>
          </ThemeProvider>
        </SidebarStyleContext.Provider>
      </UiDensityContext.Provider>
    </DebugThemeContext.Provider>
  )
}

if (!import.meta.env.VITE_DISABLE_SENTRY) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || "missing_dsn",
    sendDefaultPii: true,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT ||
      import.meta.env.MODE ||
      "development",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate:
      parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 1.0,
    tracePropagationTargets: import.meta.env
      .VITE_SENTRY_TRACE_PROPAGATION_TARGETS
      ? import.meta.env.VITE_SENTRY_TRACE_PROPAGATION_TARGETS.split(",")
      : ["localhost"],
    enableMetrics: import.meta.env.VITE_SENTRY_ENABLE_METRICS === "true",
    replaysSessionSampleRate:
      parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE) ||
      0.1,
    replaysOnErrorSampleRate:
      parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE) ||
      1.0,
  })
}

const App = () => {
  return (
    <StrictMode>
      <AppAuth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        authorizationParams={{
          audience: "mbls-global-api",
          redirect_uri: window.location.origin,
        }}
        cacheLocation="localstorage"
      >
        <Provider store={store}>
          <AppWrapper />
        </Provider>
      </AppAuth0Provider>
    </StrictMode>
  )
}

const root = createRoot(document.getElementById("root")!, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack)
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
})
root.render(<App />)
